import axios from "axios"
import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import Select from "react-select"
import DatePicker from "react-datepicker"
import "react-datepicker/dist/react-datepicker.css"
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap"
import { setBreadcrumbItems } from "store/actions"
import config from "config/config"
import SnackbarComponent from "components/SnackBar"
import "../../newcomponents/index.css"

const History = () => {
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)
  const dispatch = useDispatch()
  const mode = useSelector(state => state.Layout.layoutMode)

  const [activities, setActivities] = useState([])
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedUser, setSelectedUser] = useState(null)
  const [selectedActionType, setSelectedActionType] = useState(null)
  const [startDate, setStartDate] = useState(null)
  const [endDate, setEndDate] = useState(null)

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)

  // Fetch data
  const fetchHistory = async () => {
    if (!activeOrganization?.id || !userData?.token) return
    try {
      setLoading(true)
      const response = await axios.get(
        `${config.backendUrl}/activity/getHistories?orgId=${activeOrganization.id}`,
        {
          headers: { Authorization: `Bearer ${userData.token}` },
        },
      )
      setActivities(response.data.activities || [])
    } catch (err) {
      console.error(err)
      setErr("Error, could not fetch activities.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (activeOrganization) {
      const breadcrumbItems = [
        {
          title: `${activeOrganization.name} (${activeOrganization.type})`,
          link: "/",
        },
        { title: "History", link: "#" },
      ]
      dispatch(setBreadcrumbItems(`History`, breadcrumbItems))
      fetchHistory()
    }
  }, [activeOrganization])

  const handleClose = () => {
    setErr(null)
    setSuccess(null)
  }

  // Filtering
  const userOptions = [
    ...new Set(activities.map(act => act.User?.name).filter(Boolean)),
  ].map(name => ({ label: name, value: name }))

  const actionTypeOptions = [
    ...new Set(activities.map(act => act.actionType).filter(Boolean)),
  ].map(type => ({ label: type, value: type }))

  const filteredActivities = activities.filter(activity => {
    const matchUser = selectedUser
      ? activity.User?.name === selectedUser.value
      : true
    const matchAction = selectedActionType
      ? activity.actionType === selectedActionType.value
      : true
    const activityDate = activity.createdAt
      ? new Date(activity.createdAt)
      : null
    const matchDate =
      (!startDate || (activityDate && activityDate >= startDate)) &&
      (!endDate || (activityDate && activityDate <= endDate))
    return matchUser && matchAction && matchDate
  })

  // Pagination logic
  const totalRecords = filteredActivities.length
  const totalPages = Math.ceil(totalRecords / itemsPerPage)
  const indexOfLastItem = currentPage * itemsPerPage
  const indexOfFirstItem = indexOfLastItem - itemsPerPage
  const currentItems = filteredActivities.slice(
    indexOfFirstItem,
    indexOfLastItem,
  )

  const generatePageNumbers = () => {
    const pages = []
    const maxVisible = 5
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i)
    } else {
      let start = Math.max(1, currentPage - 2)
      let end = Math.min(totalPages, currentPage + 2)
      if (start > 1) pages.unshift("...")
      if (end < totalPages) pages.push("...")
      for (let i = start; i <= end; i++) pages.splice(pages.length - 1, 0, i)
      if (!pages.includes(1)) pages.unshift(1)
      if (!pages.includes(totalPages)) pages.push(totalPages)
    }
    return pages
  }

  const pages = generatePageNumbers()

  return (
    <div>
      <SnackbarComponent
        err={err}
        success={success}
        setErr={setErr}
        setSuccess={setSuccess}
      />

      {/* Filters */}
      <Row
        style={{
          borderRadius: "20px",
          padding: "20px 0px",
          margin: "0px 0px",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          backgroundColor: mode === "dark" ? "#252f40" : "#ffffff",
        }}
        className="mb-3"
      >
        <Col md={4}>
          <Label>Performed By</Label>
          <Select
            options={userOptions}
            value={selectedUser}
            onChange={setSelectedUser}
            isClearable
            placeholder="Select User"
            menuPortalTarget={document.body} // 👈 render dropdown in body (above everything)
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }), // 👈 ensures the dropdown is visible above sticky header
              control: base => ({
                ...base,
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                borderColor: "#d1d5db",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#2990DF",
                },
              }),
              singleValue: base => ({
                ...base,
                color: mode === "dark" ? "#fff" : "#000",
              }),
              menu: base => ({
                ...base,
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                zIndex: 9999,
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#2990DF"
                  : state.isFocused
                    ? mode === "dark"
                      ? "#333"
                      : "#f0f0f0"
                    : "transparent",
                color: state.isSelected
                  ? "#fff"
                  : mode === "dark"
                    ? "#fff"
                    : "#000",
                cursor: "pointer",
              }),
            }}
          />
        </Col>

        <Col md={4}>
          <Label>Action Type</Label>
          <Select
            options={actionTypeOptions}
            value={selectedActionType}
            onChange={setSelectedActionType}
            isClearable
            placeholder="Select Action Type"
            menuPortalTarget={document.body} // 👈 makes the dropdown render above everything
            styles={{
              menuPortal: base => ({ ...base, zIndex: 9999 }), // 👈 ensures visibility
              control: base => ({
                ...base,
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                borderColor: "#d1d5db",
                boxShadow: "none",
                "&:hover": {
                  borderColor: "#2990DF",
                },
              }),
              singleValue: base => ({
                ...base,
                color: mode === "dark" ? "#fff" : "",
              }),
              menu: base => ({
                ...base,
                backgroundColor: mode === "dark" ? "#252f40" : "#fff",
                zIndex: 9999,
              }),
              option: (base, state) => ({
                ...base,
                backgroundColor: state.isSelected
                  ? "#2990DF"
                  : state.isFocused
                    ? mode === "dark"
                      ? "#333"
                      : "#f0f0f0"
                    : "transparent",
                color: state.isSelected
                  ? "#fff"
                  : mode === "dark"
                    ? "#fff"
                    : "#000",
                cursor: "pointer",
              }),
            }}
          />
        </Col>

        <Col md={2}>
          <Label>Start Date</Label>
          <DatePicker
            selected={startDate}
            onChange={date => setStartDate(date)}
            className="form-control"
            placeholderText="Start Date"
            dateFormat="yyyy-MM-dd"
          />
        </Col>
        <Col md={2}>
          <Label>End Date</Label>
          <DatePicker
            selected={endDate}
            onChange={date => setEndDate(date)}
            className="form-control"
            placeholderText="End Date"
            dateFormat="yyyy-MM-dd"
          />
        </Col>
      </Row>

      {/* Table */}
      <Row>
        <Col xl={12}>
          <Card
            style={{
              borderRadius: "20px",
              backgroundColor: mode === "dark" ? "#252F40" : "#ffffff",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
            }}
          >
            <CardHeader
              style={{
                borderTopLeftRadius: "20px",
                borderTopRightRadius: "20px",
                backgroundColor: mode === "dark" ? "#252F40" : "#fff",
              }}
            >
              <CardTitle
                style={{
                  color: mode === "dark" ? "#ffffff" : "#2990DF",
                }}
              >
                Activities
              </CardTitle>
            </CardHeader>
            <CardBody>
              {loading ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "30vh",
                  }}
                >
                  <Spinner
                    style={{
                      color: mode === "dark" ? "#fff" : "#2990DF",
                      height: "3rem",
                      width: "3rem",
                    }}
                  >
                    Loading...
                  </Spinner>
                </div>
              ) : activities && activities.length > 0 ? (
                <>
                  <div
                    className="table-responsive"
                    style={{
                      maxHeight: "40vh",
                      overflowY: "auto",
                    }}
                  >
                    <Table
                      className="custom-activity-table"
                      style={{
                        backgroundColor: mode === "dark" ? "#252F40" : "#fff",
                      }}
                    >
                      <thead className="sticky-header">
                        <tr>
                          <th className="text-center">Performed By</th>
                          <th className="text-center">Activity</th>
                          <th className="text-center">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.map((activity, index) => (
                          <tr key={index} style={{ cursor: "pointer" }}>
                            <td className="text-center">
                              {activity.User?.name || "—"}
                            </td>
                            <td className="text-center">
                              {activity.action || "—"}
                            </td>
                            <td className="text-center">
                              {activity.createdAt
                                ? new Date(
                                    activity.createdAt,
                                  ).toLocaleDateString("en-US", {
                                    weekday: "long",
                                    year: "numeric",
                                    month: "short",
                                    day: "2-digit",
                                  })
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                </>
              ) : (
                <div
                  className="text-center"
                  style={{
                    height: "30vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  No Activities Performed
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
      <Row>
        <Col xl={12}>
          {/* Pagination */}
          <div
            style={{
              borderRadius: "20px",
              boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
              backgroundColor: mode === "dark" ? "#252f40" : "#fff",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              //marginTop: "10px",
              padding: "20px",
              color: mode === "dark" ? "#9ca3af" : "#6b7280",
              fontSize: "14px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "10px",
              }}
            >
              <span>Showing</span>
              <select
                value={itemsPerPage}
                onChange={e => {
                  setItemsPerPage(Number(e.target.value))
                  setCurrentPage(1)
                }}
                style={{
                  padding: "6px 30px 6px 12px",
                  border: `1px solid ${
                    mode === "dark" ? "#3a4556" : "#d1d5db"
                  }`,
                  borderRadius: "6px",
                  background: mode === "dark" ? "#1a2332" : "#fff",
                  color: mode === "dark" ? "#fff" : "#000",
                  fontSize: "14px",
                  cursor: "pointer",
                  outline: "none",
                  appearance: "none",
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 8px center",
                }}
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>of {totalRecords} records</span>
            </div>

            <div
              style={{
                display: "flex",
                gap: "4px",
                alignItems: "center",
              }}
            >
              {/* First & Prev */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: "transparent",
                  color:
                    currentPage === 1
                      ? mode === "dark"
                        ? "#4b5563"
                        : "#d1d5db"
                      : mode === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "18px",
                }}
              >
                «
              </button>
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: "transparent",
                  color:
                    currentPage === 1
                      ? mode === "dark"
                        ? "#4b5563"
                        : "#d1d5db"
                      : mode === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontSize: "18px",
                }}
              >
                ‹
              </button>

              {/* Page numbers */}
              {pages.map((page, index) =>
                page === "..." ? (
                  <span
                    key={`ellipsis-${index}`}
                    style={{
                      padding: "6px 12px",
                      color: mode === "dark" ? "#9ca3af" : "#6b7280",
                    }}
                  >
                    ...
                  </span>
                ) : (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    style={{
                      padding: "6px 12px",
                      border: "none",
                      borderRadius: "6px",
                      background:
                        currentPage === page ? "#2990DF" : "transparent",
                      color:
                        currentPage === page
                          ? "#fff"
                          : mode === "dark"
                            ? "#9ca3af"
                            : "#6b7280",
                      cursor: "pointer",
                      minWidth: "36px",
                      fontWeight: currentPage === page ? "600" : "400",
                    }}
                  >
                    {page}
                  </button>
                ),
              )}

              {/* Next & Last */}
              <button
                onClick={() =>
                  setCurrentPage(prev => Math.min(prev + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: "transparent",
                  color:
                    currentPage === totalPages
                      ? mode === "dark"
                        ? "#4b5563"
                        : "#d1d5db"
                      : mode === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "18px",
                }}
              >
                ›
              </button>
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: "6px 10px",
                  border: "none",
                  background: "transparent",
                  color:
                    currentPage === totalPages
                      ? mode === "dark"
                        ? "#4b5563"
                        : "#d1d5db"
                      : mode === "dark"
                        ? "#9ca3af"
                        : "#6b7280",
                  cursor:
                    currentPage === totalPages ? "not-allowed" : "pointer",
                  fontSize: "18px",
                }}
              >
                »
              </button>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default History
